import React, { useState, useEffect, useRef } from "react";
import { Sparkles, Trophy, Star, ArrowRight, X, Award, MapPin, RotateCcw, ChevronRight } from "lucide-react";
import "../pages/CutoffExplorer.css";

export default function CelebrationIntroModal({ isOpen, onClose }) {
  const [step, setStep] = useState(1);
  const [isClosing, setIsClosing] = useState(false);
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  const startAnimation = () => {
    clearTimers();
    setStep(1);

    // Step 1: 0 to 3.0s (Heartiest Congratulations)
    const t1 = setTimeout(() => {
      setStep(2);
    }, 3000);

    // Step 2: 3.0s to 6.0s (861+ MBBS Admissions Milestone)
    const t2 = setTimeout(() => {
      setStep(3);
    }, 6000);

    // Step 3: 6.0s onwards -> Stays indefinitely on screen until user clicks skip / enter!
    timersRef.current = [t1, t2];
  };

  useEffect(() => {
    if (!isOpen) return;

    startAnimation();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "Enter") {
        if (step === 3) {
          handleClose();
        } else {
          setStep((prev) => Math.min(prev + 1, 3));
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimers();
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleClose = () => {
    clearTimers();
    setIsClosing(true);
    setTimeout(() => {
      if (onClose) onClose();
      setIsClosing(false);
    }, 350);
  };

  const goToStep = (s) => {
    clearTimers();
    setStep(s);
  };

  const handleReplay = (e) => {
    e.stopPropagation();
    startAnimation();
  };

  if (!isOpen) return null;

  return (
    <div className={`ce-intro-overlay ${isClosing ? "fade-out" : ""}`}>
      {/* Background Animated Glows & Confetti */}
      <div className="ce-intro-bg-glow glow-1" />
      <div className="ce-intro-bg-glow glow-2" />
      <div className="ce-intro-sparkles">
        {[...Array(24)].map((_, i) => (
          <span
            key={i}
            className="ce-intro-particle"
            style={{
              left: `${(i * 17) % 100}%`,
              top: `${(i * 23) % 100}%`,
              animationDelay: `${i * 0.15}s`,
              animationDuration: `${2.5 + (i % 3)}s`
            }}
          />
        ))}
      </div>

      {/* Top Skip Button */}
      <div className="ce-intro-top-bar">
        <button onClick={handleClose} className="ce-intro-skip-btn">
          Skip to Cutoffs <ArrowRight size={13} />
        </button>
      </div>

      {/* Main Centered Content */}
      <div className="ce-intro-card">
        {/* Brand Stamp */}
        <div className="ce-intro-brand">
          <img
            src="/landing-assets/images/branding/logo1.png"
            alt="Admissions Made Easy"
            className="ce-intro-logo"
          />
          <span className="ce-intro-badge">
            <Sparkles size={11} className="text-amber-400" /> NEET UG 2026 MILESTONE
          </span>
        </div>

        {/* STEP 1: CONGRATULATIONS (3 seconds) */}
        {step === 1 && (
          <div className="ce-intro-step step-1 animate-in">
            <div className="ce-intro-trophy-wrap">
              <Trophy size={42} className="text-amber-400" />
            </div>
            <h1 className="ce-intro-heading">
              Heartiest <span className="ce-gold-text">Congratulations!</span>
            </h1>
            <p className="ce-intro-sub">
              To all the aspiring doctors &amp; medical students who secured their dream seats in NEET 2026!
            </p>
            <div className="ce-intro-chips-row">
              <span className="ce-intro-chip">🩺 MBBS</span>
              <span className="ce-intro-chip">🦷 BDS</span>
              <span className="ce-intro-chip">🌿 BAMS</span>
              <span className="ce-intro-chip">🌸 BHMS</span>
            </div>
            <div style={{ marginTop: "0.75rem", display: "flex", justifyContent: "center" }}>
              <button 
                onClick={() => goToStep(2)} 
                className="ce-intro-step-next-btn"
                title="Next slide"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: 861+ ADMISSIONS MILESTONE (3 seconds) */}
        {step === 2 && (
          <div className="ce-intro-step step-2 animate-in">
            <div className="ce-intro-counter-box">
              <span className="ce-intro-counter-num">861<sup>+</sup></span>
              <span className="ce-intro-counter-lbl">MBBS &amp; Medical Seats Secured</span>
            </div>
            <h2 className="ce-intro-heading-2">
              Proudly Guided by <span className="ce-gold-text">Admissions Made Easy</span>
            </h2>
            <p className="ce-intro-sub">
              With SML &amp; AIR Precision Preference Filling — ensuring every rank achieves its highest possible college!
            </p>
            <div className="ce-intro-chips-row">
              <span className="ce-intro-chip">🏛️ Govt. Medical Colleges</span>
              <span className="ce-intro-chip">⭐ Top Private Institutes</span>
              <span className="ce-intro-chip">🎯 Deemed Universities</span>
            </div>
            <div style={{ marginTop: "0.75rem", display: "flex", justifyContent: "center" }}>
              <button 
                onClick={() => goToStep(3)} 
                className="ce-intro-step-next-btn"
                title="Next slide"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: BRAND AUTHORITY & READY TO ENTER (Stays indefinitely until clicked) */}
        {step === 3 && (
          <div className="ce-intro-step step-3 animate-in">
            <div className="ce-intro-seal">
              <Award size={36} className="text-amber-400" />
            </div>
            <h2 className="ce-intro-heading-2">
              Maharashtra's <span className="ce-gold-text">#1 Medical Admission Mentor</span>
            </h2>
            <div className="ce-intro-branches">
              <MapPin size={13} className="text-amber-400" />
              <span>Offices: Latur · Pune · Solapur · Kolhapur · Nanded</span>
            </div>
            <p className="ce-intro-sub">
              Since 2011 — 14+ Years of Guiding Medical Aspirants to Success.
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, flexWrap: "wrap", marginTop: "0.4rem" }}>
              <button onClick={handleClose} className="ce-intro-enter-btn">
                Explore Live Cutoffs Now <ArrowRight size={14} />
              </button>
              <button onClick={handleReplay} className="ce-intro-replay-btn" title="Replay celebration from start">
                <RotateCcw size={13} /> Replay
              </button>
            </div>
          </div>
        )}

        {/* ── SEGMENTED 3-STEP PROGRESS & NAVIGATION ────────────────── */}
        <div className="ce-intro-stepper-footer">
          <div className="ce-intro-step-indicators">
            <button 
              type="button"
              className={`ce-intro-step-dot ${step === 1 ? "active" : (step > 1 ? "completed" : "")}`}
              onClick={() => goToStep(1)}
              title="Step 1: Congratulations"
            >
              <span className="ce-intro-dot-fill dot-fill-1" />
              <span className="ce-intro-dot-label">1. Congrats</span>
            </button>
            <button 
              type="button"
              className={`ce-intro-step-dot ${step === 2 ? "active" : (step > 2 ? "completed" : "")}`}
              onClick={() => goToStep(2)}
              title="Step 2: 861+ Milestone"
            >
              <span className="ce-intro-dot-fill dot-fill-2" />
              <span className="ce-intro-dot-label">2. 861+ Milestone</span>
            </button>
            <button 
              type="button"
              className={`ce-intro-step-dot ${step === 3 ? "active final-step" : ""}`}
              onClick={() => goToStep(3)}
              title="Step 3: Explore Cutoffs"
            >
              <span className="ce-intro-dot-fill dot-fill-3" />
              <span className="ce-intro-dot-label">3. Explore</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
